import type { LogMealBatchArgs, MealType } from "./meal-review/review-utils";

// MealForm pure logic (plan todo 18). The component supports two flows:
// - "legacy" (Today sheet): calories REQUIRED, submits entries.createLoggedFood.
// - "quickAdd" (logging-flow quick add): calories OPTIONAL — a blank calorie
//   field means UNKNOWN (kcalUnknown: true, estimatedKcal OMITTED, never 0) —
//   and submits a single-item api.meals.logMealBatch with source "manual" and
//   dataSource "user-entered".
// Everything interactive funnels through this module so the repo's node-env
// test pattern (renderToStaticMarkup + pure functions, NO jsdom) can assert
// both flows. Nothing here ever zero-fills a blank field.

export interface MealFormFields {
  readonly name: string;
  readonly kcal: string;
  readonly protein: string;
  readonly carbs: string;
  readonly fat: string;
  readonly fiber: string;
  readonly notes: string;
}

export const EMPTY_MEAL_FORM: MealFormFields = {
  name: "",
  kcal: "",
  protein: "",
  carbs: "",
  fat: "",
  fiber: "",
  notes: "",
};

export type MealFormFlow = "legacy" | "quickAdd";

// Blank -> undefined (unknown); otherwise a finite non-negative number or
// undefined. Shared by both flows so "" never becomes 0.
export function parseNonnegativeMetric(value: string): number | undefined {
  if (value.trim().length === 0) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}

function getMetricError(value: string, label: string): string | null {
  if (value.trim().length === 0) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? null : `${label} must be non-negative.`;
}

// First validation error, or null when the form is submittable. Legacy keeps
// its historical messages byte-identical; quickAdd drops only the required
// calorie rule (blank = unknown) but still rejects a non-numeric/negative
// calorie entry.
export function validateMealForm(form: MealFormFields, flow: MealFormFlow): string | null {
  if (form.name.trim().length === 0) return "Add a meal name first.";
  if (flow === "legacy" && form.kcal.trim().length === 0) return "Calories are required.";
  if (form.kcal.trim().length > 0 && parseNonnegativeMetric(form.kcal) === undefined) {
    return "Calories must be non-negative.";
  }
  const metrics = [
    [form.protein, "Protein"],
    [form.carbs, "Carbs"],
    [form.fat, "Fat"],
    [form.fiber, "Fiber"],
  ] as const;
  for (const [value, label] of metrics) {
    const metricError = getMetricError(value, label);
    if (metricError !== null) return metricError;
  }
  return null;
}

// Exact api.meals.logMealBatch single-item payload for the quick-add flow
// (convex/lib/mealValidators batchArgs). Blank calories -> kcalUnknown: true
// with NO estimatedKcal/finalKcal; every blank macro stays ABSENT; notes ride
// on the item only when non-empty. Nothing is zero-filled.
export function buildQuickAddLogArgs(input: {
  readonly form: MealFormFields;
  readonly date: number;
  readonly clientDayStart: number;
  readonly clientLoggedAt: number;
  readonly mealType: MealType;
}): LogMealBatchArgs {
  const { form } = input;
  const estimatedKcal = parseNonnegativeMetric(form.kcal);
  const proteinG = parseNonnegativeMetric(form.protein);
  const carbsG = parseNonnegativeMetric(form.carbs);
  const fatG = parseNonnegativeMetric(form.fat);
  const fiberG = parseNonnegativeMetric(form.fiber);
  const notes = form.notes.trim();
  return {
    date: input.date,
    clientDayStart: input.clientDayStart,
    clientLoggedAt: input.clientLoggedAt,
    mealType: input.mealType,
    source: "manual",
    items: [
      {
        name: form.name.trim(),
        ...(estimatedKcal === undefined
          ? { kcalUnknown: true }
          : { estimatedKcal, finalKcal: estimatedKcal }),
        ...(proteinG === undefined ? {} : { proteinG }),
        ...(carbsG === undefined ? {} : { carbsG }),
        ...(fatG === undefined ? {} : { fatG }),
        ...(fiberG === undefined ? {} : { fiberG }),
        ...(notes.length > 0 ? { notes } : {}),
        dataSource: "user-entered",
      },
    ],
  };
}
